// ─────────────────────────────────────────────────────────────────────────
// InstallPWAPrompt.tsx
// ─────────────────────────────────────────────────────────────────────────
// Small banner that appears at the bottom of the driver job-sheet page,
// offering to install the PWA to the home screen, plus a toast when a new
// version has been cached and is ready to use.
//
// Two install paths, because there's no single cross-platform API:
//   • Android / desktop Chrome/Edge → native `beforeinstallprompt` event.
//     We capture it, stash it, and trigger it ourselves from our own button
//     (the browser's default mini-infobar is suppressed by preventDefault()).
//   • iOS Safari → Apple has never shipped `beforeinstallprompt`. The ONLY
//     way to install is the manual "Share → Add to Home Screen" flow, so we
//     detect iOS + "not already installed" and show instructions instead.
//
// Usage: render <InstallPWAPrompt /> once near the root of
// OnlineJobSheetPage.tsx (e.g. just above the action bar).
// ─────────────────────────────────────────────────────────────────────────

import { useEffect, useState, useCallback } from 'react';
import { DownloadSimple, X, ShareFat, PlusSquare, ArrowsClockwise } from '@phosphor-icons/react';

// Minimal type for the event TypeScript's lib.dom doesn't ship by default.
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

/** True if already running as an installed PWA (standalone display mode). */
function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    // iOS Safari's older, non-standard flag
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

export default function InstallPWAPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showAndroidBanner, setShowAndroidBanner] = useState(false);
  const [showIosBanner, setShowIosBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [updateReady, setUpdateReady] = useState(false);
  const [offlineReady, setOfflineReady] = useState(false);

  // ── Capture the native Android/desktop install prompt ──
  useEffect(() => {
    if (isStandalone()) return; // already installed, nothing to offer

    function handleBeforeInstallPrompt(e: Event) {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowAndroidBanner(true);
    }
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // iOS never fires beforeinstallprompt — show manual instructions instead,
    // but only once per browser (localStorage flag) so it isn't nagging.
    if (isIos() && !localStorage.getItem('rs_ios_install_hint_dismissed')) {
      setShowIosBanner(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  // ── Service worker update / offline-ready notifications ──
  // Provided by vite-plugin-pwa's virtual module (only resolvable inside a
  // Vite build, hence the dynamic import + try/catch so this file doesn't
  // break type-checking/linting in non-Vite contexts).
  useEffect(() => {
    let cleanup: (() => void) | undefined;
    (async () => {
      try {
        // @ts-expect-error — virtual module injected by vite-plugin-pwa at build time
        const { registerSW } = await import('virtual:pwa-register');
        const updateSW = registerSW({
          onNeedRefresh() {
            setUpdateReady(true);
          },
          onOfflineReady() {
            setOfflineReady(true);
            window.setTimeout(() => setOfflineReady(false), 3500);
          },
        });
        cleanup = () => {
          // no explicit teardown API; nothing to clean up currently
          void updateSW;
        };
      } catch {
        // Not running under Vite/vite-plugin-pwa (e.g. plain dev server) — skip silently.
      }
    })();
    return () => cleanup?.();
  }, []);

  const handleInstallClick = useCallback(async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    // Whether accepted or dismissed, the prompt can only be used once.
    setDeferredPrompt(null);
    setShowAndroidBanner(false);
  }, [deferredPrompt]);

  const handleDismissIos = useCallback(() => {
    setShowIosBanner(false);
    localStorage.setItem('rs_ios_install_hint_dismissed', '1');
  }, []);

  const handleReload = useCallback(() => {
    window.location.reload();
  }, []);

  if (dismissed || isStandalone()) return null;

  return (
    <>
      {/* ── Android / desktop native install banner ── */}
      {showAndroidBanner && (
        <div className="fixed inset-x-0 bottom-20 z-40 mx-auto flex max-w-md items-center gap-3 rounded-2xl border border-brand-200 bg-white px-4 py-3 shadow-2xl">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-brand-100 text-brand-800">
            <DownloadSimple weight="bold" className="text-xl" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-gray-900">ติดตั้งแอปลงหน้าจอโฮม</p>
            <p className="text-[11px] text-gray-500">เปิดใช้งานได้เร็วขึ้น เหมือนแอปทั่วไป</p>
          </div>
          <button
            type="button"
            onClick={handleInstallClick}
            className="flex-shrink-0 rounded-xl bg-brand-900 px-3.5 py-2 text-xs font-bold text-white active:scale-95"
          >
            ติดตั้ง
          </button>
          <button
            type="button"
            aria-label="ปิด"
            onClick={() => { setShowAndroidBanner(false); setDismissed(true); }}
            className="flex-shrink-0 text-gray-300 hover:text-gray-500"
          >
            <X weight="bold" />
          </button>
        </div>
      )}

      {/* ── iOS manual "Add to Home Screen" instructions ── */}
      {showIosBanner && (
        <div className="fixed inset-x-0 bottom-20 z-40 mx-auto flex max-w-md items-start gap-3 rounded-2xl border border-brand-200 bg-white px-4 py-3 shadow-2xl">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-brand-100 text-brand-800">
            <ShareFat weight="bold" className="text-xl" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-gray-900">ติดตั้งลงหน้าจอโฮม</p>
            <p className="mt-0.5 text-[11px] leading-relaxed text-gray-500">
              แตะปุ่ม <ShareFat weight="bold" className="inline text-brand-600" /> ด้านล่างจอ แล้วเลือก
              <span className="mx-1 inline-flex items-center gap-0.5 font-semibold text-brand-700">
                <PlusSquare weight="bold" /> เพิ่มไปยังหน้าจอโฮม
              </span>
            </p>
          </div>
          <button
            type="button"
            aria-label="ปิด"
            onClick={handleDismissIos}
            className="flex-shrink-0 text-gray-300 hover:text-gray-500"
          >
            <X weight="bold" />
          </button>
        </div>
      )}

      {/* ── New version cached — prompt reload ── */}
      {updateReady && (
        <div className="fixed inset-x-0 bottom-20 z-40 mx-auto flex max-w-md items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 shadow-2xl">
          <ArrowsClockwise weight="bold" className="flex-shrink-0 text-xl text-amber-700" />
          <p className="flex-1 text-xs font-semibold text-amber-800">มีเวอร์ชันใหม่ — แตะเพื่ออัปเดต</p>
          <button
            type="button"
            onClick={handleReload}
            className="flex-shrink-0 rounded-xl bg-amber-600 px-3.5 py-2 text-xs font-bold text-white active:scale-95"
          >
            อัปเดต
          </button>
        </div>
      )}

      {/* ── Offline-ready confirmation (brief, auto-dismiss) ── */}
      {offlineReady && (
        <div className="fixed inset-x-0 bottom-20 z-40 mx-auto flex max-w-md items-center gap-2 rounded-2xl border border-green-200 bg-green-50 px-4 py-2.5 text-xs font-semibold text-green-800 shadow-lg">
          ✓ พร้อมใช้งานแบบออฟไลน์แล้ว
        </div>
      )}
    </>
  );
}
